/**
 * A stand-in room for the lab preview: you, plus five simulated people whose
 * maps come from the examples. It lives in memory only — nothing is stored and
 * nothing is shared with anyone. A live session replaces this with the
 * session's stored responses; the screen (`MentalMapRoom`) does not change.
 */

import { useCallback, useMemo, useState } from 'react';

import { MAP_EXAMPLES } from './examples';
import type { MapRoom, MapRoomPerson } from './MentalMapRoom';
import { EMPTY_MAP, newMapId, type MapDoc, type SharedModel } from './model';
import type { SharedRoom } from './SharedModels';

const ME = 'me';

/** Simulated people: who they are, which example they drew, whether they showed it early. */
const OTHERS: { id: string; name: string; example: number; shown: boolean; done: boolean }[] = [
  { id: 'p1', name: 'Ana', example: 0, shown: true, done: true },
  { id: 'p2', name: 'Ben', example: 1, shown: true, done: true },
  { id: 'p3', name: 'Chidi', example: 2, shown: false, done: false },
  { id: 'p4', name: 'Dee', example: 3, shown: true, done: true },
  { id: 'p5', name: 'Eli', example: 0, shown: false, done: false },
];

export function usePreviewRoom(prompt: string): MapRoom {
  const [myDoc, setMyDoc] = useState<MapDoc>(EMPTY_MAP);
  const [myMapShown, setMyMapShown] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const [myDone, setMyDone] = useState(false);
  const [timerEndsAt, setTimerEndsAt] = useState<number | null>(null);

  const people = useMemo<MapRoomPerson[]>(
    () => [
      { id: ME, name: 'You', doc: revealed || myMapShown ? myDoc : null, done: myDone, started: myDoc.shapes.length > 0 },
      ...OTHERS.map((o) => ({
        id: o.id,
        name: o.name,
        doc: revealed || o.shown ? MAP_EXAMPLES[o.example].doc : null,
        done: o.done,
        started: true,
      })),
    ],
    [myDoc, myMapShown, revealed, myDone],
  );

  const revealAll = useCallback(() => setRevealed(true), []);
  const setTimer = useCallback((seconds: number | null) => setTimerEndsAt(seconds ? Date.now() + seconds * 1000 : null), []);

  // Shared models, in memory. In the preview you always hold the pen.
  const [models, setModels] = useState<SharedModel[]>([]);
  const shared = useMemo<SharedRoom>(
    () => ({
      models,
      createModel: async (input) => {
        const id = newMapId('m');
        setModels((current) => [
          ...current,
          {
            id,
            title: input.title,
            level: input.level,
            parentModelId: input.parentModelId ?? null,
            parentShapeId: input.parentShapeId ?? null,
            doc: EMPTY_MAP,
            penHolderId: ME,
          },
        ]);
        return id;
      },
      takePen: async (modelId) => models.find((m) => m.id === modelId)?.doc ?? null,
      saveModel: (modelId, doc) =>
        setModels((current) => current.map((m) => (m.id === modelId ? { ...m, doc } : m))),
    }),
    [models],
  );

  return { prompt, meId: ME, isHost: true, myDoc, myMapShown, revealed, people, shared, saveMyDoc: setMyDoc, setMyMapShown, revealAll, myDone, setMyDone, timerEndsAt, setTimer };
}
